import { t } from '@/i18n';
import { collectSSE, safeJson } from './sse';
import { providerFetch } from './transport';
import { LLMError, type ChatMeta, type ChatRequest, type ContentPart, type LLMAdapter } from './types';

type ClaudePart = { type: 'text'; text: string } | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } };

function toPart(p: ContentPart): ClaudePart {
  return p.type === 'text' ? { type: 'text', text: p.text } : { type: 'image', source: { type: 'base64', media_type: p.mimeType, data: p.base64 } };
}

interface StreamEvent {
  type?: string;
  delta?: { type?: string; text?: string };
  error?: { message?: string };
  message?: { usage?: { input_tokens?: number } };
  usage?: { output_tokens?: number };
}

const MAX_TOKENS = 8192;
const MAX_TOKENS_RESEARCH = 16000;
// Dynamic filtering needs Claude 4.6+; the basic tool works on every model, so it is the fallback on a 400.
const WEB_TOOL = 'web_search_20260318';
const WEB_TOOL_BASIC = 'web_search_20250305';
const MAX_PAUSE_TURNS = 4;

/** The non-streaming message shape we read back from web-enabled calls (only the fields we use). */
export interface ClaudeMessage {
  content?: (
    | { type: 'text'; text: string; citations?: { type?: string; url?: string; title?: string }[] }
    | { type: 'server_tool_use'; name?: string; input?: { query?: string } }
    | { type: string }
  )[];
  stop_reason?: string;
  usage?: { input_tokens?: number; output_tokens?: number; server_tool_use?: { web_search_requests?: number } };
}

/** Pulls text, the queries Claude ran, and cited pages out of one message. Pure; exported for tests. */
export function extractClaudeMessage(msg: ClaudeMessage): { text: string; queries: string[]; sources: { url: string; title?: string }[] } {
  let text = '';
  const queries: string[] = [];
  const sources: { url: string; title?: string }[] = [];
  for (const block of msg.content ?? []) {
    if (block.type === 'text' && 'text' in block) {
      text += block.text;
      for (const c of block.citations ?? []) if (c.url) sources.push({ url: c.url, title: c.title });
    } else if (block.type === 'server_tool_use' && 'input' in block && block.input?.query) {
      queries.push(block.input.query);
    }
  }
  return { text, queries, sources };
}

function dedupeSources(list: { url: string; title?: string }[]): { url: string; title?: string }[] {
  const seen = new Set<string>();
  return list.filter((s) => (seen.has(s.url) ? false : (seen.add(s.url), true)));
}

/**
 * Web-enabled call: non-streaming so the server-side search loop can be resumed on `pause_turn` by echoing the
 * assistant content back (encrypted search results included). Usage is summed across the resumed turns.
 */
async function chatWithSearch(req: ChatRequest): Promise<string> {
  const run = async (toolType: string): Promise<string> => {
    const convo: unknown[] = req.messages.map((m) => ({ role: m.role, content: m.content.map(toPart) }));
    const meta: Required<Pick<ChatMeta, 'inputTokens' | 'outputTokens' | 'searches'>> & { queries: string[]; sources: { url: string; title?: string }[] } = { inputTokens: 0, outputTokens: 0, searches: 0, queries: [], sources: [] };
    let text = '';
    for (let turn = 0; turn < MAX_PAUSE_TURNS; turn++) {
      const body = {
        model: req.model,
        system: req.system,
        max_tokens: MAX_TOKENS_RESEARCH,
        messages: convo,
        tools: [{ type: toolType, name: 'web_search', max_uses: req.webSearch!.maxUses }],
      };
      const res = await providerFetch('claude', '/v1/messages', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: req.signal }, req.apiKey);
      const msg = (await res.json()) as ClaudeMessage;
      const part = extractClaudeMessage(msg);
      text += part.text;
      meta.queries.push(...part.queries);
      meta.sources.push(...part.sources);
      meta.inputTokens += msg.usage?.input_tokens ?? 0;
      meta.outputTokens += msg.usage?.output_tokens ?? 0;
      meta.searches += msg.usage?.server_tool_use?.web_search_requests ?? 0;
      if (msg.stop_reason !== 'pause_turn') break;
      convo.push({ role: 'assistant', content: msg.content });
    }
    req.onMeta?.({ ...meta, sources: dedupeSources(meta.sources) });
    if (text) req.onDelta?.(text);
    return text;
  };
  try {
    return await run(WEB_TOOL);
  } catch (e) {
    if (e instanceof LLMError && e.status === 400 && /web_search|tool/i.test(e.message)) return run(WEB_TOOL_BASIC);
    throw e;
  }
}

export const claudeAdapter: LLMAdapter = {
  provider: 'claude',
  async chat(req) {
    if (req.webSearch) return chatWithSearch(req);
    const body = { model: req.model, system: req.system, max_tokens: MAX_TOKENS, stream: true, messages: req.messages.map((m) => ({ role: m.role, content: m.content.map(toPart) })) };
    const res = await providerFetch('claude', '/v1/messages', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), signal: req.signal }, req.apiKey);
    const usage: ChatMeta = {};
    const text = await collectSSE(res, (data) => {
      const ev = safeJson(data) as StreamEvent | undefined;
      if (!ev) return undefined;
      if (ev.type === 'error') throw new LLMError('upstream', ev.error?.message ?? t('llm.streamError'));
      if (ev.type === 'message_start' && ev.message?.usage?.input_tokens != null) usage.inputTokens = ev.message.usage.input_tokens;
      if (ev.type === 'message_delta' && ev.usage?.output_tokens != null) usage.outputTokens = ev.usage.output_tokens;
      if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') return ev.delta.text;
      return undefined;
    }, req.onDelta);
    req.onMeta?.(usage);
    return text;
  },
  async listModels(apiKey, signal) {
    const json = (await (await providerFetch('claude', '/v1/models?limit=100', { signal }, apiKey)).json()) as { data?: { id: string; created_at?: string }[] };
    return (json.data ?? []).sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')).map((m) => m.id);
  },
};
